import React, {useEffect, useRef, useState} from "react";
import TuiImageEditor from "tui-image-editor";
import {DialogActions, DialogContent} from "@mui/material";
import Dialog from "../../base/dialog";
import Button from "../../base/button/button";
import {createUUId, dataUrlToArrayBuffer} from "../../../../core/services/utils/utils";
import Utils from "../../../../core/services/utils/utils";
import useIsMounted from "../../../hooks/use-is-mounted";
import {InputFileAcceptTypes} from "../../../../core/constants/enums";

/** JPEG quality for editor exports (0–1). Keeps photo payloads small vs default PNG. */
const EDIT_IMAGE_JPEG_QUALITY = 0.85;

const IMAGE_EXTENSION_PATTERN = /\.(png|jpe?g|webp|heic|heif|gif|bmp)$/i;

/**
 * Ensures the exported filename uses a .jpg extension to match JPEG export.
 * @param {string} fileName
 * @return {string}
 */
const normalizeJpegFileName = (fileName) => {
    if (IMAGE_EXTENSION_PATTERN.test(fileName)) {
        return fileName.replace(IMAGE_EXTENSION_PATTERN, '.jpg');
    }
    return `${fileName}.jpg`;
};

/**
 * Gets the imageEditor options for instantiation.
 * @param {any} props any extra props passed here
 * @return {*&{usageStatistics: boolean, includeUI: {initMenu: string, usageStatistics: boolean, menuBarPosition: string, menu: string[], loadImage: {path: string, name: string}}}}
 */
const getOptions = (props) => {
    return {
        includeUI: {
            menu: [
                "crop",
                "flip",
                "rotate",
                "draw",
                "shape",
                "icon",
                "text",
                "mask",
                "filter"
            ],
            initMenu: null,
            // uiSize: {
            //     width: "100%",
            //     height: "80vh"
            // },
            // theme: {},
            menuBarPosition: "top",
            loadImage: {
                path: 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7',
                name: 'Blank',
            },
            usageStatistics: false,
        },
        selectionStyle: {
            cornerSize: 20,
            rotatingPointOffset: 70
        },
        cssMaxWidth: 700,
        cssMaxHeight: 500,
        // selectionStyle: {
        //     cornerStyle: 'string',
        //     cornerSize: 'number',
        //     cornerColor: 'string',
        //     cornerStrokeColor: 'string',
        //     transparentCorners: 'boolean',
        //     lineWidth: 'number',
        //     borderColor: 'string',
        //     rotatingPointOffset: 'number',
        // },
        ...props,
        usageStatistics: false,
    }
}

const EditImageDialog = ({
                             open,
                             setOpen,
                             url,
                             file,
                             onFinished,
                             edit = false,
                             onlySubmitWhenDone = false,
                             ...props
                         }) => {
    const [processing, setProcessing] = useState(false);
    const [changed, setChanged] = useState(false);
    const isMounted = useIsMounted();
    /**@type {React.MutableRefObject<import('tui-image-editor').ImageEditor>}*/
    const imageEditor = useRef(null);
    const id = useRef(`_${createUUId()}`);

    const name = url
        ? url?.split('/')[url?.split('/').length - 1]
        : file?.name

    /**
     * Listens for the changes in open and with each change:
     * - if not open, then disposes of any controller or resources
     * - else, creates a new imageEditor and loads the given image.
     */
    useEffect(() => {
        const _unmount = () => {
            unbindEventHandlers();
            imageEditor.current?.destroy();
            imageEditor.current = null;
            setProcessing(false);
            setChanged(false);
        }
        if (!open) return _unmount;
        const options = getOptions(props);
        setTimeout(() => {
            if (!isMounted()) return;
            imageEditor.current = new TuiImageEditor(`#${id.current}`, options);
            bindEventHandlers(props, imageEditor.current);
            hideUnnecessaryElements()
            loadImage()
        }, 100)
        return _unmount;
    }, [open])

    /**
     * Loads the given image based off of its type (file or url), and displays it in the canvas.
     */
    const loadImage = () => {
        waitUntilImageEditorIsUnlocked(imageEditor.current).then(async () => {
            if (url?.length) {
                let fetched = url;
                if (url.endsWith(InputFileAcceptTypes.heif) || url.endsWith(InputFileAcceptTypes.heic)) {
                    let blobRes = await fetch(url)
                    let blob = await blobRes.blob()
                    const heic2any = require('heic2any');
                    let conversionResult = await heic2any({blob, quality: 1})
                    fetched = URL.createObjectURL(conversionResult);
                }
                imageEditor.current.loadImageFromURL(fetched, name).then((r) => resize(null, r));
            } else {
                if (file.name.endsWith(InputFileAcceptTypes.heif) || file.name.endsWith(InputFileAcceptTypes.heic) || file.type === 'image/heic' || file.type === 'image/heif') {
                    let fetched = file;
                    const url = URL.createObjectURL(file);
                    let blobRes = await fetch(url)
                    let blob = await blobRes.blob()
                    const heic2any = require('heic2any');
                    let conversionResult = await heic2any({blob, quality: 1})
                    fetched = URL.createObjectURL(conversionResult);
                    imageEditor.current.loadImageFromURL(fetched, name).then((r) => resize(null, r));
                } else {
                    imageEditor.current.loadImageFromFile(file, name).then((r) => resize(null, r));
                }

            }
        })
    }

    /**
     * Resizes the imageEditor with the provided results.
     * @param {any} result the sizeChange results.
     * @param {Event} e the event
     * @return {Promise<void>}
     */
    const resize = async (e = null, result = null) => {
        if (result) {
            await imageEditor.current.ui.resizeEditor({
                imageSize: {
                    oldWidth: result.oldWidth,
                    oldHeight: result.oldHeight,
                    newWidth: result.newWidth,
                    newHeight: result.newHeight
                },
            });
        } else {
            await imageEditor.current.ui.resizeEditor({
                uiSize: {
                    width: parseInt(`${window.innerWidth}`.replace('px', '')) * 0.5,
                    height: parseInt(`${window.innerHeight}`.replace('px', '')) * 0.5,
                }
            });
        }
    }

    /**
     * Awaits for the imageEditor to become free before using it for any operation.
     * @param {any} imageEditor the imageEditor instance
     * @return {Promise<unknown>}
     */
    const waitUntilImageEditorIsUnlocked = (imageEditor) => new Promise((resolve) => {
        const interval = setInterval(() => {
            if (!imageEditor._invoker._isLocked) {
                clearInterval(interval);
                resolve();
            }
        }, 100);
    })

    /**
     * Binds any event listeners based off of the given props.
     * @param props
     * @param imageEditor
     */
    const bindEventHandlers = (props, imageEditor) => {
        window.addEventListener('resize', resize)
        Object.keys(props)
            .filter(isEventHandlerKeys)
            .forEach((key) => {
                const eventName = key[2].toLowerCase() + key.slice(3);
                imageEditor.off(eventName)
                imageEditor.on(eventName, props[key]);
            });
    }

    /**
     * Hides the elements related to the logo and the control buttons to reducer the clutter of the rendered ui.
     */
    const hideUnnecessaryElements = () => {
        document.getElementsByClassName('tui-image-editor-header-logo').item(0).classList.add('d-none')
        document.getElementsByClassName('tui-image-editor-controls-buttons').item(0).classList.add('d-none')
    }

    /**
     * Un-binds any event listeners that were bound before unmounting the component.
     */
    const unbindEventHandlers = () => {
        window.removeEventListener('resize', resize);
        Object.keys(props)
            .filter(isEventHandlerKeys)
            .forEach((key) => {
                const eventName = key[2].toLowerCase() + key.slice(3);
                imageEditor.current.off(eventName);
            });
    }

    /**
     * Determines whether the given key is a name appropriate for an event listener.
     * @param {string}key
     * @return {boolean}
     */
    const isEventHandlerKeys = (key) => /on[A-Z][a-zA-Z]+/.test(key)

    /**
     * Fetches the changes in the file before closing the dialog and gives it to the onFinish callback.
     *
     * - this method converts the given image into a dataUrl, arrayBuffer, and a name object before calling the
     * onFinish callback with these args
     * - if there is no change, just closes the dialog
     * @return {Promise<void>}
     */
    const getEditedFile = async (done = false) => {
        if (!changed && edit) {
            setProcessing(false);
            return setOpen(false);
        }
        setProcessing(true);
        const dataUrl = imageEditor.current?.toDataURL({
            format: 'jpeg',
            quality: EDIT_IMAGE_JPEG_QUALITY,
        });
        if (dataUrl) {
            const buffer = await dataUrlToArrayBuffer(dataUrl);
            const rawName = url
                ? url.split('/')[url.split('/').length - 1]
                : file?.name
            const name = rawName?.length
                ? Utils.sanitizeFileName(normalizeJpegFileName(rawName))
                : rawName;
            if (buffer && name?.length) {
                if (onlySubmitWhenDone && done) {
                    onFinished({
                        name,
                        buffer,
                        dataUrl,
                    });
                } else if (!onlySubmitWhenDone) {
                    onFinished({
                        name,
                        buffer,
                        dataUrl,
                    });
                }

            }
        }
        setProcessing(false);
        setOpen(false);
    }

    return (
        <>
            <Dialog
                open={open}
                setOpen={() => getEditedFile(false)}
                disableBackdropClick={processing}
                disableEscapeKeyDown={processing}
                loading={processing}
                maxWidth={'md'}
                className={'edit-image-dialog bound-dialog'}
            >
                <DialogContent dividers={true} className={'flex-grow-1'}>
                    <div
                        onClick={() => setChanged(true)}
                        className={'min-vh-80'} id={id.current}
                    />
                </DialogContent>
                <DialogActions className={'d-flex align-items-center justify-content-between'} sx={{padding: 0}}>
                    <Button
                        disabled={!open || processing}
                        variant={'dialog'}
                        color={'primary'}
                        onClick={() => getEditedFile(false)}
                        borderRadius={'0 8px 0 8px'}
                    >
                        Close
                    </Button>
                    <Button
                        disabled={!open || processing}
                        variant={'dialog'}
                        color={'primary'}
                        onClick={() => getEditedFile(true)}
                        borderRadius={'8px 0 8px 0'}
                    >
                        {processing ? 'Processing...' : "Done"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}


export default EditImageDialog;
